import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Security, LoginCallback } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { CssBaseline, Box, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppProvider } from './contexts/AppContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ValidationProvider } from './contexts/ValidationContext';
import { config } from './config';
import { apiClient } from './services/api';
import { useApp } from './contexts/AppContext';

// Lazy-loaded pages for code splitting
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Settings = lazy(() => import('./pages/Settings'));
const ValidationRunPage = lazy(() => import('./components/ValidationRunPage'));

// Loading fallback
const PageLoader: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <Typography>Loading...</Typography>
  </Box>
);

// Initialize Okta Auth
const oktaAuth = new OktaAuth(config.okta);

// Wire the token getter and unauthorized handler at module level so every
// API call — including those fired before Home.tsx mounts — always uses a
// fresh token from the Okta SDK rather than a potentially stale localStorage value.
apiClient.setTokenGetter(() => oktaAuth.getAccessToken());
apiClient.setOnUnauthorized(() => oktaAuth.signInWithRedirect());

// Preferences loader — runs once after auth, no UI
const PreferencesLoader: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { setPreferences } = useApp();

  useEffect(() => {
    if (isAuthenticated) {
      apiClient.getPreferences().then(setPreferences).catch(console.error);
    }
  }, [isAuthenticated, setPreferences]);

  return null;
};

// Protected Route Wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Main App Component — Okta auth is always active (local and deployed)
const App: React.FC = () => {
  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
    window.location.replace(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  return (
    <Router>
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <AuthProvider>
          <AppProvider>
            <ValidationProvider>
              <ThemeProvider>
                <CssBaseline />
                <PreferencesLoader />
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/login/callback" element={<LoginCallback />} />
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <Home />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <Settings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/validation"
                      element={
                        <ProtectedRoute>
                          <ValidationRunPage />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </Suspense>
              </ThemeProvider>
            </ValidationProvider>
          </AppProvider>
        </AuthProvider>
      </Security>
    </Router>
  );
};

export default App;
